import { IArchive } from 'common/interfaces/hierarchy-entities';
import { apiCallBegan } from 'client/store/middleware/api/api';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { toast } from '@oliasoft-open-source/react-ui-library';
import { t } from 'i18next';
import translations from 'client/internationalization/translation-map.json';
import {
  HierarchyEntityName, postHierarchyEntityAPICallAction, putHierarchyEntityAPICallAction, deleteHierarchyEntityAPICallAction,
} from '../helpers/hierarchy-api-actions-factory';

const initialState = {
  isLoading: false,
  error: null as string | null,
  archives: null as IArchive[] | null,
};

const slice = createSlice({
  name: 'object',
  initialState,
  reducers: {
    itemsRequested: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    itemsRecieved: (state, action: PayloadAction<IArchive[]>) => {
      state.archives = action.payload;
      state.isLoading = false;
    },
    itemRecieved: (state, action: PayloadAction<IArchive>) => {
      state.isLoading = false;
      if (!state.archives) {
        state.archives = [action.payload];
        return;
      }
      const updatedIndex = state.archives.findIndex((arc => arc.archiveid === action.payload.archiveid));
      if (updatedIndex < 0) {
        state.archives.push(action.payload);
      } else {
        state.archives.splice(updatedIndex, 1, action.payload);
      }
    },
    itemsFailed: (state, action: PayloadAction<{ message: string; }>) => {
      state.isLoading = false;
      state.error = action.payload.message;
      if (action.payload.message.includes('403')) {
        toast({
          message: {
            type: 'Error',
            icon: true,
            content: t(translations.authorization_notAuthorized),
          },
        });
      }
    },
    itemDeleteRecieved: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      if (!state.archives) return;

      const removedIndex = state.archives.findIndex((arc => arc.archiveid === action.payload));
      if (removedIndex < 0) return;
      state.archives.splice(removedIndex, 1);
    },
    resetArchives: (state) => {
      state.archives = initialState.archives;
    },
  },
});

export const {
  itemsRequested: archivesRequested,
  itemsRecieved: archivesRecieved,
  itemsFailed: archivesFailed,
  itemRecieved: archiveRecieved,
  itemDeleteRecieved: archiveDeleteRecieved,
  resetArchives,
} = slice.actions;

export default slice.reducer;

export const getArchives = () => apiCallBegan({
  url: '/archive/all',
  method: 'GET',
  onStart: () => ({ type: archivesRequested.type }),
  onSuccess: (response: IArchive[]) => ({
    type: archivesRecieved.type,
    payload: response,
  }),
  onError: (e: any) => ({ type: archivesFailed.type, payload: { message: e.message } }),
});

export const createArchive = (data: Partial<IArchive>) => postHierarchyEntityAPICallAction(HierarchyEntityName.ARCHIVE, data, archivesRequested.type, archivesFailed.type, archiveRecieved.type);

export const updateArchive = (data: any) => putHierarchyEntityAPICallAction(HierarchyEntityName.ARCHIVE, data.archiveid, data, archivesRequested.type, archivesFailed.type, archiveRecieved.type);

export const deleteArchive = (data: Partial<IArchive>) => deleteHierarchyEntityAPICallAction(HierarchyEntityName.ARCHIVE, data.archiveid!, archivesRequested.type, archivesFailed.type, archiveDeleteRecieved.type);
